import { Button } from '@design/components/button/Button';
import { useFilterActions } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { useScenarioAssistant } from '@terminal/widgets/scenario/components/scenario-assistant-modal/use-scenario-assistant/useScenarioAssistant';
import { useScenarioStore } from '@terminal/widgets/scenario/hooks/use-scenario-store/useScenarioStore';
import { useTranslation } from 'react-i18next';

import styles from './ClearAllButton.module.scss';

export interface ClearAllButtonProps {
  onAbort: () => void;
  onClearScenarioTable: () => void;
}

export const ClearAllButton = ({
  onAbort,
  onClearScenarioTable,
}: ClearAllButtonProps) => {
  const { t } = useTranslation('scenario');

  const {
    clearAllFilterOptions,
    setLastConditions,
    activeConditionOptions,
    activeColumnFilterOptions,
    activeAssetFilterOptions,
    scenarioPageEntityTags,
    clearScenarioPageEntityTags,
    setLastEntitiesTags,
    setLastScenariosData,
    setHasExceededUsageLimit,
    dismissScenarioFallback,
  } = useScenarioStore(state => ({
    activeConditionOptions:
      state.filterGroups['SCENARIO_CONDITIONS'].activeFilterOptions,
    activeColumnFilterOptions:
      state.filterGroups['COLUMN_FILTERS'].activeFilterOptions,
    activeAssetFilterOptions:
      state.filterGroups['ASSET_FILTERS'].activeFilterOptions,
    clearAllFilterOptions: state.clearAllColumnsFilters,
    setLastConditions: state.setLastConditions,
    scenarioPageEntityTags: state.scenarioPageEntityTags,
    clearScenarioPageEntityTags: state.clearScenarioPageEntityTags,
    setLastEntitiesTags: state.setLastEntitiesTags,
    setLastScenariosData: state.setLastScenariosData,
    setHasExceededUsageLimit: state.setHasExceededUsageLimit,
    dismissScenarioFallback: state.dismissScenarioFallback,
  }));

  const {
    resetActiveFilterOptions: resetActiveScenarioConditionsFilterOptions,
  } = useFilterActions({
    store: useScenarioStore,
    group: 'SCENARIO_CONDITIONS',
  });

  const { resetActiveFilterOptions: resetActiveAssetFilterOptions } =
    useFilterActions({
      store: useScenarioStore,
      group: 'ASSET_FILTERS',
    });

  const { resetConditions, conditions: scenarioAssistantConditions } =
    useScenarioAssistant(state => ({
      resetConditions: state.resetConditions,
      conditions: state.conditions,
    }));

  const clearAllHandler = () => {
    onAbort();
    clearAllFilterOptions();
    resetActiveScenarioConditionsFilterOptions();
    resetActiveAssetFilterOptions();
    resetConditions();
    setLastConditions(undefined);
    setLastEntitiesTags(undefined);
    onClearScenarioTable();
    clearScenarioPageEntityTags();
    setLastScenariosData([]);
    setHasExceededUsageLimit(false);
    dismissScenarioFallback();
  };

  const shouldShowClearAll =
    !!activeConditionOptions.length ||
    !!activeColumnFilterOptions.length ||
    !!scenarioAssistantConditions.length ||
    !!activeAssetFilterOptions.length ||
    !!scenarioPageEntityTags?.length;

  if (!shouldShowClearAll) {
    return null;
  }

  return (
    <Button
      className={styles.styledButton}
      data-testid="clear-all-btn"
      label={t('scenario:assets.clearAll')}
      iconName="Clear"
      variant="empty"
      size="small"
      onClick={clearAllHandler}
    />
  );
};
